import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SearchBoxCore, SessionToken } from "@mapbox/search-js-core";
import mapboxgl from "mapbox-gl";
import type { PlaceFeature, PlacePreviewFeature } from "~/lib/api";
import { mapPlaceFeatureFromMapboxPoi, suggestPlaceType } from "~/lib/mapboxUtils";
import type { SearchBoxFeatureSuggestion, SearchBoxSuggestion } from "./types";
import { filterPlacesByQuery, filterPlacesByTypeAndProximity, isNearExistingPlace, parseCoordinates } from "./utils";

const DEBOUNCE_MS = 300;
const DEFAULT_ZOOM = 15;
const FIT_BOUNDS_PADDING = 80;

/** Merge local places with deduplicated Mapbox features into unified results */
function mergeSearchResults(
  mapboxFeatures: SearchBoxFeatureSuggestion[],
  localPlaces: PlaceFeature[],
): PlacePreviewFeature[] {
  // Deduplicate: remove Mapbox features near local places
  const localCoords = localPlaces.map((p) => p.geometry.coordinates as [number, number]);
  const dedupedMapboxFeatures = mapboxFeatures.filter(
    (f) => !isNearExistingPlace(f.geometry.coordinates as [number, number], localCoords),
  );

  // Convert to PlacePreviewFeature
  const localResults = localPlaces as PlacePreviewFeature[];
  const mapboxResults = dedupedMapboxFeatures.map((f) => {
    const props = f.properties;
    return mapPlaceFeatureFromMapboxPoi({
      name: props.name,
      coordinates: f.geometry.coordinates as [number, number],
      suggestedType: suggestPlaceType({ maki: props.maki }),
      address: props.full_address || props.place_formatted || props.address,
    });
  });

  return [...localResults, ...mapboxResults];
}

/** Unified suggestion type for dropdown during Step 1 (typing) */
export type SearchSuggestion =
  | { type: "local"; place: PlaceFeature }
  | { type: "mapbox"; suggestion: SearchBoxSuggestion }
  | { type: "coordinate"; coords: [number, number] };

interface UseMapSearchOptions {
  accessToken: string;
  map: mapboxgl.Map | null;
  /** Local places to include in search (adventure map + noma.to) */
  places?: PlaceFeature[];
}

export function useMapSearch({ accessToken, map, places = [] }: UseMapSearchOptions) {
  const [query, setQuery] = useState("");
  const [mapboxSuggestions, setMapboxSuggestions] = useState<SearchBoxSuggestion[]>([]);
  const [results, setResults] = useState<PlacePreviewFeature[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const searchBoxRef = useRef<SearchBoxCore | null>(null);
  const sessionTokenRef = useRef<SessionToken | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (accessToken) {
      searchBoxRef.current = new SearchBoxCore({ accessToken });
      sessionTokenRef.current = new SessionToken();
    }
  }, [accessToken]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      abortControllerRef.current?.abort();
    };
  }, []);

  // Local places matching the search query (fuzzy search)
  const matchingLocalPlaces = useMemo(() => filterPlacesByQuery(places, query), [places, query]);

  // Step 1: Unified suggestions (local places first, then deduplicated Mapbox)
  const suggestions = useMemo<SearchSuggestion[]>(() => {
    const localSuggestions: SearchSuggestion[] = matchingLocalPlaces.map((place) => ({
      type: "local",
      place,
    }));

    // Check for coordinate input
    const coords = query.trim() ? parseCoordinates(query) : null;
    if (coords) {
      return [...localSuggestions, { type: "coordinate", coords }];
    }

    // Deduplicate: remove Mapbox suggestions that match local place names
    const localNames = new Set(matchingLocalPlaces.map((p) => p.properties.name.toLowerCase()));
    const dedupedMapboxSuggestions = mapboxSuggestions.filter((s) => !localNames.has(s.name.toLowerCase()));

    const mapboxItems: SearchSuggestion[] = dedupedMapboxSuggestions.map((suggestion) => ({
      type: "mapbox",
      suggestion,
    }));

    return [...localSuggestions, ...mapboxItems];
  }, [matchingLocalPlaces, mapboxSuggestions, query]);

  const flyToCoordinates = useCallback(
    (coords: [number, number], zoom = DEFAULT_ZOOM) => {
      map?.flyTo({ center: coords, zoom, duration: 1000 });
    },
    [map],
  );

  const fitBoundsToCoordinates = useCallback(
    (coords: [number, number][]) => {
      if (!map || coords.length === 0) return;

      if (coords.length === 1) {
        flyToCoordinates(coords[0]);
        return;
      }

      const bounds = new mapboxgl.LngLatBounds();
      coords.forEach((c) => bounds.extend(c));
      map.fitBounds(bounds, { padding: FIT_BOUNDS_PADDING, duration: 1000 });
    },
    [map, flyToCoordinates],
  );

  const fetchMapboxSuggestions = useCallback(
    async (searchText: string) => {
      if (!searchBoxRef.current || !sessionTokenRef.current || searchText.trim().length < 2) {
        setMapboxSuggestions([]);
        return;
      }

      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      setIsLoading(true);
      try {
        const center = map?.getCenter();
        const response = await searchBoxRef.current.suggest(searchText, {
          sessionToken: sessionTokenRef.current,
          signal: abortControllerRef.current.signal,
          limit: 5,
          proximity: center ? [center.lng, center.lat] : undefined,
        });
        setMapboxSuggestions(response.suggestions);
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          console.error("Search suggest error:", err);
          setMapboxSuggestions([]);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [map],
  );

  const search = useCallback(
    (value: string) => {
      setQuery(value);
      setResults([]);

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (!value.trim()) {
        setMapboxSuggestions([]);
        return;
      }

      // Don't fetch Mapbox suggestions for coordinate input
      if (parseCoordinates(value)) {
        setMapboxSuggestions([]);
        return;
      }

      debounceTimerRef.current = setTimeout(() => fetchMapboxSuggestions(value), DEBOUNCE_MS);
    },
    [fetchMapboxSuggestions],
  );

  const selectSuggestion = useCallback(
    async (suggestion: SearchSuggestion) => {
      setMapboxSuggestions([]);

      switch (suggestion.type) {
        case "local": {
          const place = suggestion.place;
          setQuery(place.properties.name);
          setResults([place as PlacePreviewFeature]);
          flyToCoordinates(place.geometry.coordinates as [number, number]);
          break;
        }

        case "coordinate": {
          const [lng, lat] = suggestion.coords;
          const name = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
          setQuery(name);
          setResults([
            mapPlaceFeatureFromMapboxPoi({
              name,
              coordinates: suggestion.coords,
              suggestedType: null,
            }),
          ]);
          flyToCoordinates(suggestion.coords);
          break;
        }

        case "mapbox": {
          const mapboxSuggestion = suggestion.suggestion;
          setQuery(mapboxSuggestion.name);

          if (!searchBoxRef.current || !sessionTokenRef.current) return;

          try {
            const response = await searchBoxRef.current.retrieve(mapboxSuggestion, {
              sessionToken: sessionTokenRef.current,
            });

            const features = response.features;
            if (features.length === 0) return;

            sessionTokenRef.current = new SessionToken();

            // Determine local places to merge based on suggestion type
            const categoryType =
              mapboxSuggestion.feature_type === "category" ? suggestPlaceType({ maki: mapboxSuggestion.maki }) : null;

            const localPlacesToMerge =
              categoryType && map
                ? filterPlacesByTypeAndProximity(places, categoryType, [map.getCenter().lng, map.getCenter().lat])
                : filterPlacesByQuery(places, mapboxSuggestion.name);

            const mergedResults = mergeSearchResults(features, localPlacesToMerge);
            setResults(mergedResults);

            // Fit bounds for multiple results
            if (mergedResults.length > 1) {
              fitBoundsToCoordinates(mergedResults.map((r) => r.geometry.coordinates as [number, number]));
            } else if (mergedResults.length === 1) {
              flyToCoordinates(mergedResults[0].geometry.coordinates as [number, number]);
            }
          } catch (err) {
            console.error("Search retrieve error:", err);
          }
          break;
        }
      }
    },
    [map, places, flyToCoordinates, fitBoundsToCoordinates],
  );

  const clear = useCallback(() => {
    setQuery("");
    setMapboxSuggestions([]);
    setResults([]);
  }, []);

  return {
    query,
    suggestions,
    results,
    isLoading,
    search,
    selectSuggestion,
    clear,
  };
}
